// src/modules/time-tracking/MyTime.js (employee self portal)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaClock, FaMapMarkerAlt, FaCircle, FaDollarSign } from 'react-icons/fa';
import EmployeeLayout from '../../layouts/EmployeeLayout';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { StatusBadge, TableContainer, StyledTable } from '../../components/styled/CommonStyles';
import { getMyCurrent, getMyShifts, formatHours, shiftSeconds, statusColor, statusLabel, activityColor } from './timeTrackingService';
import { formatDateTime, formatRelativeTime } from '../communications/utils/formatUtils';

const Banner = styled.div`
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(3)}; margin-bottom: ${(p) => p.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0,0,0,.1);
  border-left: 4px solid ${(p) => (p.active ? '#84c959' : p.theme.colors.inputGray)};
  .row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .h { color: ${(p) => p.theme.colors.textPrimary}; font-size: 1.2rem; font-weight: 600; }
  .meta { color: ${(p) => p.theme.colors.textSecondary}; margin-top: 6px; font-size: ${(p) => p.theme.typography.fontSize.small}; }
`;
const ClickRow = styled.tr`cursor: pointer;`;
const PayButton = styled.button`
  background: ${(p) => p.theme.colors.primary}; color: ${(p) => p.theme.colors.black};
  border: none; border-radius: ${(p) => p.theme.borderRadius}; cursor: pointer; font-weight: 500;
  padding: ${(p) => p.theme.spacing(1)} ${(p) => p.theme.spacing(2)};
  display: inline-flex; align-items: center; gap: 8px; margin-bottom: ${(p) => p.theme.spacing(3)};
`;

const MyTime = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getMyCurrent().catch(() => ({ active: false })), getMyShifts().catch(() => ({ shifts: [] }))])
      .then(([cur, list]) => { setCurrent(cur); setShifts(list.shifts || []); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <EmployeeLayout title="My Time">
      {loading ? <LoadingSpinner /> : (
        <>
          <Banner active={current?.active}>
            <div className="row">
              <FaCircle style={{ color: current?.active ? '#84c959' : '#7f8c8d', fontSize: 12 }} />
              <span className="h">{current?.active ? 'Clocked in' : 'Not clocked in'}</span>
              {current?.active && current?.lastPing?.activity && (
                <StatusBadge color={activityColor(current.lastPing.activity)}>{current.lastPing.activity}</StatusBadge>
              )}
            </div>
            {current?.active && (
              <div className="meta">
                Since {formatDateTime(current.shift?.clock_in_at)} · {formatHours(shiftSeconds(current.shift))}
                {current.segment?.label && <> · <FaMapMarkerAlt /> {current.segment.label}</>}
                {current.lastPing && <> · last update {formatRelativeTime(current.lastPing.recorded_at)}</>}
              </div>
            )}
            {!current?.active && <div className="meta">Use the mobile app to clock in on site.</div>}
          </Banner>

          <PayButton onClick={() => navigate('/my-time/pay')}><FaDollarSign /> View My Pay</PayButton>

          <h3 style={{ color: '#fff', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaClock /> My shifts
          </h3>
          <TableContainer>
            <StyledTable>
              <thead>
                <tr><th>Date</th><th>Clock In</th><th>Clock Out</th><th>Duration</th><th>Status</th></tr>
              </thead>
              <tbody>
                {shifts.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24 }}>No shifts yet.</td></tr>
                ) : shifts.map((s) => (
                  <ClickRow key={s.id} onClick={() => navigate(`/my-time/shifts/${s.id}`)}>
                    <td>{formatDateTime(s.clock_in_at).split(',')[0]}</td>
                    <td>{formatDateTime(s.clock_in_at)}</td>
                    <td>{s.clock_out_at ? formatDateTime(s.clock_out_at) : '—'}</td>
                    <td>{formatHours(shiftSeconds(s))}</td>
                    <td><StatusBadge color={statusColor(s.status)}>{statusLabel(s.status)}</StatusBadge></td>
                  </ClickRow>
                ))}
              </tbody>
            </StyledTable>
          </TableContainer>
        </>
      )}
    </EmployeeLayout>
  );
};

export default MyTime;
